import { PRESENTER_OPTIONS } from '../PresenterCheckButton';
import { useMemo, useState, type JSX } from 'react';
import { ListFilter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { useOutreachInviters } from '../../hooks/bridge';
import { ACCOUNT_TYPE_ORDER, DISCOVERY_METHOD_ORDER, accountTypeLabel, discoveryMethodLabel } from '../AccountTypeBadge';
import { OUTREACH_STATE_ORDER, outreachStateLabel } from '../OutreachStatus';
import { DebouncedInput, Field as FilterField } from '../../workspace/ui';
import { activeFilterCount, clearedFilters, type LeadUrlState } from './leadFilterModel';

const TRI_EMAIL = [
  { value: 'yes', label: 'Has email' },
  { value: 'no', label: 'No email' },
];
const TRI_DONE = [
  { value: 'no', label: 'Open' },
  { value: 'yes', label: 'Done' },
];

/**
 * Server-side filters for a folder's leads. The four most used sit in the
 * first row; the rest fold behind "More filters". Every change resets to
 * page 1 (and, via the filter key, clears any selection).
 */
export function LeadFilters({
  state,
  onChange,
}: {
  state: LeadUrlState;
  onChange: (patch: Partial<LeadUrlState>) => void;
}): JSX.Element {
  const inviters = useOutreachInviters();
  const active = activeFilterCount(state);
  const moreActive = !!(state.type || state.foundVia || state.minF || state.maxF || state.minEng || state.maxEng || state.email !== 'any');
  const [more, setMore] = useState(moreActive);
  const set = (patch: Partial<LeadUrlState>) => onChange({ ...patch, page: 1 });

  const assigneeOptions = useMemo(
    () => [
      { value: 'none', label: 'Unassigned' },
      ...(inviters.data ?? []).filter((i) => i.isActive).map((i) => ({ value: String(i.id), label: i.name, hint: i.team })),
    ],
    [inviters.data],
  );

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="Search" className="min-w-[12rem] flex-1">
          <DebouncedInput placeholder="@username or name" value={state.q} onCommit={(q) => set({ q })} />
        </FilterField>
        <FilterField label="On camera">
          <Combobox className="w-full md:w-40" value={state.presenter} onChange={(presenter) => set({ presenter })} allowAll allLabel="All creators" options={PRESENTER_OPTIONS} />
        </FilterField>
        <FilterField label="Outreach">
          <Combobox
            className="w-full md:w-44"
            value={state.state}
            onChange={(v) => set({ state: v })}
            allowAll
            allLabel="Any status"
            options={OUTREACH_STATE_ORDER.map((s) => ({ value: s, label: outreachStateLabel(s) }))}
          />
        </FilterField>
        <FilterField label="Done">
          <Combobox
            className="w-full md:w-32"
            value={state.done === 'any' ? '' : state.done}
            onChange={(v) => set({ done: v || 'any' })}
            allowAll
            allLabel="All"
            options={TRI_DONE}
          />
        </FilterField>
        <FilterField label="Assigned to">
          <Combobox
            className="w-full md:w-44"
            value={state.assignee}
            onChange={(v) => set({ assignee: v })}
            allowAll
            allLabel="Anyone"
            options={assigneeOptions}
            loading={inviters.isLoading}
          />
        </FilterField>
        <Button variant="outline" size="sm" className="mb-0.5" aria-expanded={more} onClick={() => setMore((v) => !v)}>
          <ListFilter className="h-3.5 w-3.5" />
          More filters
        </Button>
        {active > 0 && (
          <Button variant="ghost" size="sm" className="mb-0.5" onClick={() => onChange(clearedFilters(state))}>
            Clear {active}
          </Button>
        )}
      </div>
      {more && (
        <div className="flex flex-wrap items-end gap-3">
          <FilterField label="Type">
            <Combobox
              className="w-full md:w-40"
              value={state.type}
              onChange={(v) => set({ type: v })}
              allowAll
              allLabel="Any type"
              options={ACCOUNT_TYPE_ORDER.map((t) => ({ value: t, label: accountTypeLabel(t) }))}
            />
          </FilterField>
          <FilterField label="Found via">
            <Combobox
              className="w-full md:w-40"
              value={state.foundVia}
              onChange={(v) => set({ foundVia: v })}
              allowAll
              allLabel="Any source"
              options={DISCOVERY_METHOD_ORDER.map((m) => ({ value: m, label: discoveryMethodLabel(m) }))}
            />
          </FilterField>
          <FilterField label="Followers">
            <span className="flex items-center gap-1.5">
              <DebouncedInput type="number" placeholder="Min" className="w-24" value={state.minF} onCommit={(minF) => set({ minF })} />
              <span className="text-muted-foreground">–</span>
              <DebouncedInput type="number" placeholder="Max" className="w-24" value={state.maxF} onCommit={(maxF) => set({ maxF })} />
            </span>
          </FilterField>
          <FilterField label="Engagement %">
            <span className="flex items-center gap-1.5">
              <DebouncedInput type="number" step={0.1} placeholder="Min" className="w-20" value={state.minEng} onCommit={(minEng) => set({ minEng })} />
              <span className="text-muted-foreground">–</span>
              <DebouncedInput type="number" step={0.1} placeholder="Max" className="w-20" value={state.maxEng} onCommit={(maxEng) => set({ maxEng })} />
            </span>
          </FilterField>
          <FilterField label="Email">
            <Combobox
              className="w-full md:w-32"
              value={state.email === 'any' ? '' : state.email}
              onChange={(v) => set({ email: v || 'any' })}
              allowAll
              allLabel="Any"
              options={TRI_EMAIL}
            />
          </FilterField>
        </div>
      )}
    </div>
  );
}
